import { useState } from 'react';

import {
  CheckmarkFilled,
  Close,
  InformationFilled,
  WarningAltFilled,
  WarningFilled,
} from '@mint-lib/icons';
import {
  AlertDescription,
  Alert as AlertRoot,
  AlertTitle,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type AlertProps = React.ComponentProps<typeof AlertRoot> & {
  title?: string;
  description?: string;
  truncation?: boolean;
  tooltipTitle?: string;
  removable?: boolean;
  onClose?: () => void;
};

const colors = [
  {
    severity: 'info',
    color: 'primary',
    icon: <InformationFilled />,
  },
  {
    severity: 'warning',
    color: 'warning',
    icon: <WarningAltFilled />,
  },
  {
    severity: 'error',
    color: 'error',
    icon: <WarningFilled />,
  },
  {
    severity: 'success',
    color: 'primary',
    icon: <CheckmarkFilled />,
  },
];

function Alert({
  variant = 'multiline',
  removable = true,
  severity = 'info',
  ...props
}: AlertProps) {
  return (
    <AlertRoot
      data-type={severity}
      severity={severity}
      variant={variant}
      className={cn(props.className, 'group/alert')}
    >
      <div className={cn(variant === 'inline' && 'flex items-center')}>
        {colors.find((color) => color.severity === severity)?.icon}
      </div>
      {variant === 'inline' ? (
        <InlineAlert
          title={props.title}
          description={props.description}
          tooltipTitle={props.tooltipTitle}
          removable={removable}
          onClose={props.onClose}
        >
          {props.children}
        </InlineAlert>
      ) : (
        <MultilineAlert
          title={props.title}
          description={props.description}
          removable={removable}
          onClose={props.onClose}
        >
          {props.children}
        </MultilineAlert>
      )}
    </AlertRoot>
  );
}

const InlineAlert = ({
  title,
  description,
  tooltipTitle,
  children,
  removable,
  onClose,
}: {
  title?: string;
  description?: string;
  tooltipTitle?: string;
  children: React.ReactNode;
  removable?: boolean;
  onClose?: () => void;
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipOpenChange = (open: boolean) => {
    tooltipTitle && setTooltipOpen(open);
  };

  return (
    <div className="flex w-full items-center justify-between gap-1">
      <Tooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
        <TooltipTrigger asChild>
          <div className="inline-flex w-full items-center justify-between">
            <div className="grid grid-cols-[auto_1fr] items-center gap-1">
              {title && <AlertTitle className={'truncate'}>{title}</AlertTitle>}
              {description && (
                <AlertDescription className={'truncate'}>
                  {description}
                </AlertDescription>
              )}
            </div>
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipTitle}</TooltipContent>
      </Tooltip>
      {removable && (
        <Button
          icon
          variant="plain"
          size={'small'}
          onClick={onClose}
          className={
            'top-2 right-2 ml-1 group-data-[type=error]/alert:fill-error-dark group-data-[type=warning]/alert:fill-warning-dark'
          }
        >
          <Close aria-label="Close" />
        </Button>
      )}
    </div>
  );
};

const MultilineAlert = ({
  title,
  description,
  children,
  removable,
  onClose,
}: {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  removable?: boolean;

  onClose?: () => void;
}) => {
  return (
    <div className={cn('flex w-full justify-between gap-1')}>
      <div className={cn('grid gap-1')}>
        {title && <AlertTitle>{title}</AlertTitle>}
        {description && <AlertDescription>{description}</AlertDescription>}
        {children && <div className="inline-flex gap-1 pt-1">{children}</div>}
      </div>
      {removable && (
        <Button
          icon
          variant="plain"
          onClick={onClose}
          className={
            'top-2 right-2 group-data-[type=error]/alert:fill-error-dark group-data-[type=warning]/alert:fill-warning-dark'
          }
        >
          <Close aria-label="Close" />
        </Button>
      )}
    </div>
  );
};

export default Alert;
